import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons"
import type { TableColumnsType, TablePaginationConfig, TableProps } from "antd"
import { Button, Card, message, Modal, Select, Space, Table, Tabs, Tag } from "antd"
import Title from "antd/es/typography/Title"
import queryString from "query-string"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router"
import { Link } from "react-router-dom"
import Filters, { FilterComponent } from "~/components/Filters"
import { Paths } from "~/constants"
import { t } from "~/languages/i18n"
import { AttributesBase, BaseDataProps, PaginationMeta } from "~/types/types"
import { normalizeText } from "~/utils/helper"
import logoEn from "../../../../public/images/logo-en.png"
import logoVi from "../../../../public/images/logo-vi.png"
import baseService from "../service/base"

const List: React.FC = () => {
  const [dataItem, setDataItem] = useState<BaseDataProps[]>([])
  const filterRef = useRef<{ filterFunction: () => void } | null>(null)
  const [sortPaginationValues, setSortPaginationValues] = useState<{ [key: string]: string } | null>(null)
  const [needRefresh, setNeedRefresh] = useState<boolean>(false)
  const [filterValues, setFilterValues] = useState<{ [key: string]: string } | null>(null)
  const [defaultFilterValues, setDefaultFilterValues] = useState<{ [key: string]: any } | null>(null)
  const [baseNameOptions, setBaseNameOptions] = useState<{ value: string | number; label: string }[]>([])
  const [filterTabBase, setFilterTabBase] = useState("all")
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [bulkAction, setBulkAction] = useState<string>("")
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [metaData, setMetaData] = useState<PaginationMeta>()
  const [totalDraftsItem, setTotalDraftsItem] = useState(0)
  const [totalPublishedItem, setTotalPublishedItem] = useState(0)
  const [totalItem, setTotalItem] = useState(0)

  const baseServices = baseService()

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(selectedRowKeys)
    },
  }

  const fetchAllBases = async (searchTerms?: { [key: string]: string }) => {
    setLoading(true)
    setDataItem([])

    try {
      const defaultPagination = { page: "1", pageSize: "5" }
      const allResponse = await baseServices.fetchAllBases({ ...defaultPagination, ...searchTerms })
      const response = allResponse && allResponse.data
      const meta = allResponse && allResponse.meta
      setMetaData(meta)

      if (response && response.length > 0) {
        setDataItem(response)
        if (searchTerms === undefined) {
          setTotalItem(meta?.pagination?.total || 0)
        }
        setBaseNameOptions(
          response.map((page: { id: number; attributes: AttributesBase }) => ({
            value: page.id,
            label: page.attributes.name,
          })),
        )

        setLoading(false)
      } else {
        setLoading(false)
      }

      return response
    } catch (error) {
      message.error(t("Failed to fetch base. Please try again"))
    }
  }

  const onSearch = useCallback(
    (filters: object) => {
      const values: { [key: string]: string } = {}

      Object.keys(filters).forEach(key => {
        const value = filters[key as keyof typeof filters]
        if (typeof value === "string") {
          values[key] = value
        }
      })

      if (!values) {
        message.error(t("Values ​​are not defined"))
        return
      }

      let newValue = { ...values }

      if (values.search) {
        newValue = { ...newValue, search: normalizeText(values.search) }
      }

      const searchParams = queryString.stringify(newValue, { encode: false })
      const newUrl = searchParams.length > 0 ? `${location.pathname}?${searchParams}` : location.pathname
      navigate(newUrl)
    },
    [navigate],
  )

  useEffect(() => {
    const searchParams = queryString.parse(location.search)
    const filterValues: { [key: string]: string } = {}
    const sortPaginationValues: { [key: string]: string } = {}

    for (const key of Object.keys(searchParams)) {
      const value = searchParams[key]
      if (value && typeof value === "string") {
        if (key === "base" || key === "limit" || value === "asc" || value === "desc") {
          sortPaginationValues[key] = value
        } else {
          filterValues[key] = value
        }
      }
    }

    setSortPaginationValues(sortPaginationValues)
    setFilterValues(filterValues)
    setDefaultFilterValues(filterValues)
  }, [location.search])

  useEffect(() => {
    fetchAllBases()
  }, [needRefresh])

  useEffect(() => {
    if (!filterValues && !sortPaginationValues) return

    if (filterValues || sortPaginationValues) {
      const params = { ...filterValues, ...sortPaginationValues, publicationState: filterTabBase }
      fetchAllBases(params)
    }
  }, [filterValues, sortPaginationValues])

  const handleEdit = (id: number) => {
    navigate(`${Paths.BASESYSTEM.EDIT.PATH}/${id}`)
  }

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: t("Confirm Delete"),
      content: t("Are you sure you want to delete this base?"),
      okText: t("Delete"),
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          setLoading(true)
          await baseServices.deleteBase(id)
          fetchAllBases()
          message.success(t("The base has been successfully deleted!"))
        } catch {
          message.error(t("Handle Delete Error"))
        }
      },
    })
  }

  const handleBulkDelete = async () => {
    try {
      setLoading(true)
      const ids = selectedRowKeys.map(id => Number(id))
      await Promise.all(ids.map(id => baseServices.deleteBase(id)))
      setSelectedRowKeys([])
      fetchAllBases()
      setLoading(false)
      message.success(t("Bases deleted successfully!"))
    } catch {
      message.error(t("Handle Delete Error"))
    }
  }

  const handleApplyBulkAction = () => {
    if (bulkAction === "delete" && selectedRowKeys.length > 0) {
      Modal.confirm({
        title: t("Confirm Delete"),
        content: t("Are you sure you want to delete the selected bases?"),
        okText: t("Delete"),
        cancelText: t("Cancel"),
        onOk: handleBulkDelete,
      })
    } else if (selectedRowKeys.length === 0) {
      message.warning(t("Please select at least one base to delete."))
    } else {
      message.warning(t("Please select a valid bulk action"))
    }
  }

  const handleChangeTab = (key: string) => {
    setFilterTabBase(key)

    if (filterRef.current) {
      filterRef.current.filterFunction()
    }
    setSortPaginationValues(null)
    fetchAllBases({
      ...filterValues,
      ...sortPaginationValues,
      publicationState: key,
    })
  }

  const onChange: TableProps<BaseDataProps>["onChange"] = (pagination: TablePaginationConfig) => {
    setSortPaginationValues({
      page: pagination.current ? pagination.current.toString() : "1",
      pageSize: pagination.pageSize?.toString() || "5",
    })
  }

  const onRefresh = () => {
    if (filterRef.current) {
      filterRef.current.filterFunction()
    }
    setSortPaginationValues(null)
    setNeedRefresh(!needRefresh)
  }

  const columns: TableColumnsType<BaseDataProps> = [
    {
      title: <strong>{t("Facility name")}</strong>,
      dataIndex: ["attributes", "name"],
      width: "25%",
      key: "name",
      showSorterTooltip: false,
      sorter: (a, b) => a.attributes.name.localeCompare(b.attributes.name),
      render: (_, record) => (
        <Link
          to={`${Paths.BASESYSTEM.EDIT.PATH}/${record.id}`}
          className="text-[#1677ff] cursor-pointer transition duration-300 hover:text-[#0b5ed7] hover:underline text-base"
        >
          {record.attributes.name}
        </Link>
      ),
    },
    {
      title: <strong>{t("Email")}</strong>,
      dataIndex: ["attributes", "email"],
      width: "25%",
      key: "email",
      render: email => <span>{email}</span>,
    },
    {
      title: <strong>{t("Hotline")}</strong>,
      dataIndex: ["attributes", "hotline"],
      width: "15%",
      key: "hotline",
      render: hotline => <span>{hotline}</span>,
    },
    {
      title: <strong>{t("Status")}</strong>,
      dataIndex: ["attributes", "publishedAt"],
      key: "status",
      width: "10%",
      render: (publishedAt: string) => {
        return publishedAt ? (
          <Tag color="green" className="p-1 w-full text-center">
            {t("Active")}
          </Tag>
        ) : (
          <Tag color="error" className="p-1 w-full text-center">
            {t("Inactive")}
          </Tag>
        )
      },
    },
    {
      title: <strong>{t("Action")}</strong>,
      key: "action",
      width: "20%",
      render: (_, record) => (
        <Space size="middle" className="justify-center items-center">
          <Button
            type="primary"
            icon={<EditOutlined />}
            className="bg-blue-500 px-4 py-2"
            onClick={() => handleEdit(record.id)}
          >
            {t("Edit")}
          </Button>
          <Button danger type="primary" icon={<DeleteOutlined />} className="" onClick={() => handleDelete(record.id)}>
            <span className="">{t("Delete")}</span>
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div className="p-4 ">
      <div className="flex space-x-4 items-center">
        <Title level={2} className="m-0">
          {t("Base System")}
        </Title>
        <Button
          onClick={() => navigate(Paths.BASESYSTEM.CREATE.PATH)}
          color="primary"
          variant="outlined"
          icon={<PlusOutlined />}
        >
          {t("Add New Base")}
        </Button>
      </div>
      <div className="flex justify-between mt-3">
        <Tabs
          defaultActiveKey="all"
          activeKey={filterTabBase}
          onChange={handleChangeTab}
          items={[
            { label: `${t("All")} (${totalItem})`, key: "all" },
            { label: `${t("Draft")}`, key: "draft" },
            { label: `${t("Published")}`, key: "published" },
          ]}
        />
      </div>
      <div className="items-center">
        <Space className=" space-x-5 flex justify-between">
          <Space className="space-x-3">
            <Select
              defaultValue={t("Bulk Actions")}
              style={{ width: 170 }}
              onChange={value => setBulkAction(value)}
              options={[
                { value: "all", label: t("Bulk Actions") },
                { value: "delete", label: t("Delete") },
              ]}
            />
            <Button onClick={handleApplyBulkAction}>{t("Apply")}</Button>
          </Space>
          <Space>
            <Button onClick={onRefresh} icon={<ReloadOutlined />}>
              {t("Refresh")}
            </Button>
          </Space>
        </Space>
      </div>
      <Card type="inner" style={styles.card}>
        <Filters
          filterRef={filterRef}
          defaultFilterValues={defaultFilterValues}
          onSubmit={onSearch}
          filters={
            [
              {
                label: t("Name"),
                placeholder: t("Find Base Name"),
                name: "name",
                type: "text",
                options: baseNameOptions,
                customStyle: "2xl:!w-[500px] !w-[300px]",
              },
              {
                label: t("Language"),
                placeholder: t("Choose Language"),
                name: "locale",
                type: "select",
                options: [
                  {
                    value: "vi",
                    label: (
                      <div className="flex items-center gap-2">
                        <img src={logoVi} alt="Vietnamese" className="w-5 h-5" />
                        VI
                      </div>
                    ),
                  },
                  {
                    value: "en",
                    label: (
                      <div className="flex items-center gap-2">
                        <img src={logoEn} alt="English" className="w-5 h-5" />
                        EN
                      </div>
                    ),
                  },
                ],
                defaultValue: localStorage.getItem("language"),
              },
            ] as FilterComponent[]
          }
        />
      </Card>
      <Table
        className="mt-3  border border-[rgb(229 229 229)]"
        rowKey="id"
        rowSelection={rowSelection}
        columns={columns}
        loading={loading}
        dataSource={dataItem}
        onChange={onChange}
        pagination={{
          pageSize: 5,
          total: (metaData && metaData?.pagination?.total) || 0,
        }}
        scroll={{ x: 1200 }}
        rowClassName={(record, index) => (index % 2 === 0 ? "bg-[#f6f7f7] " : "bg-white")}
      />
    </div>
  )
}

export default List

const styles = {
  title: {
    marginTop: 0,
    marginBottom: 0,
  },
  card: {
    marginTop: "1.25rem",
    marginBottom: "1.25rem",
  },
}
